/* Shared visual identity: bright, high-contrast "club daylight" — warm white
 * surfaces, near-black ink, vivid violet accent. (The palette keys keep their
 * historical names — `brass` IS the accent — so the ~40 importing files don't
 * need to change when the palette does.)
 *
 * Accent is deliberately NOT orange: openlychess.com (a direct competitor)
 * uses vivid orange as its brand color, and green/red are reserved for
 * training feedback (correct/wrong) — a third hue keeps the accent from
 * colliding with either. Violet reads as premium/distinct among chess
 * apps (lichess/chess.com both lean green) and pairs naturally with the
 * Pro tier's crown (♛) branding.
 * Used by both the Ledger dashboard (OpeningLedger.jsx) and the Learn tab. */
export const C = {
  bg: "#FBFAF7",
  card: "#FFFFFF",
  cardDeep: "#F4F1EB",
  edge: "rgba(26,27,24,0.12)",
  faint: "rgba(26,27,24,0.08)",
  ink: "#1C1D1A",
  sub: "#6E7266",
  brass: "#6C3CE9", // the accent (vivid violet)
  brassDim: "rgba(108,60,233,0.10)",
  brassDeep: "#4E28B8", // the accent's shadow: the "edge" under a PRIMARY button
  sage: "#3D8B63",
  up: "#1F9D63", // reserved for correct/hit training feedback
  down: "#D64530", // reserved for wrong/miss training feedback
  black: "#26271F",
  onAccent: "#FFFFFF", // text/icons placed on a `brass` background
};

export const F = {
  serif: "'Iowan Old Style','Palatino Linotype',Palatino,Georgia,serif",
  sans: "-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,sans-serif",
  mono: "ui-monospace,'SF Mono',Menlo,Consolas,monospace",
};

/* Y1 · the design system: one set of shapes, tones and type for every screen.
 *
 * Pablo, 2026-09-16: "fix all styles to make them consistent". The audit that
 * followed counted 16 border radii, 31 font sizes, 20 shadows and ~30 hex
 * colours written inline, and at least five looks for a button: each screen
 * had been styled on its own, so the app read as several apps.
 *
 * The rules:
 *   - Type (T): serif for display only (page titles, course names, big
 *     numbers); bold sans for everything a user reads in order to act;
 *     uppercase sans `label`s for the small captions that used to be mono; mono
 *     only for chess data (moves, clocks, ratings, evals). Nothing below 11.5px.
 *   - Shape (R): `sm` inside a card (mini boards, icons), `md` for buttons,
 *     inputs and segmented controls, `lg` for cards, banners and sheets, `pill`
 *     for chips and tags.
 *   - Depth (ELEVATION): a card sits on a 2px solid edge; anything tappable on
 *     a 4px one, the same "press me" edge PRIMARY has. `overlay` is for sheets.
 *   - Colour: C for the neutrals and the accent; TONES for anything that means
 *     something (success, warn, danger, info, gold), each an ink for text, a
 *     ground to sit on, an edge, a solid fill and a deep shade for its edge.
 * New UI spreads these (and Ui.jsx's pieces) rather than inventing values. */
export const R = { sm: 10, md: 14, lg: 20, pill: 999 };

export const TONES = {
  accent: { ink: C.brass, ground: C.brassDim, edge: "rgba(108,60,233,0.32)", solid: C.brass, deep: C.brassDeep },
  success: { ink: "#17804F", ground: "#EAF6F0", edge: "#A8D9C0", solid: C.up, deep: "#177A4B" },
  warn: { ink: "#B85A18", ground: "#FFF4EA", edge: "#F0B37E", solid: "#E0782F", deep: "#A9520F" },
  danger: { ink: "#B7372A", ground: "#FCEEEB", edge: "#EFB3A8", solid: C.down, deep: "#A93A28" },
  info: { ink: "#2B6A92", ground: "#EAF4FB", edge: "#B9DDF2", solid: "#2F80B7", deep: "#23628C" },
  gold: { ink: "#9A6700", ground: "#FFF7E6", edge: "#F2C14E", solid: "#F59E0B", deep: "#C77C02" },
  neutral: { ink: C.ink, ground: C.cardDeep, edge: C.edge, solid: C.sub, deep: "#50544A" },
};

export const T = {
  display: { fontFamily: F.serif, fontSize: 30, fontWeight: 500, letterSpacing: -0.2, lineHeight: 1.1 },
  title: { fontFamily: F.serif, fontSize: 20, fontWeight: 500, lineHeight: 1.25 },
  heading: { fontFamily: F.sans, fontSize: 15.5, fontWeight: 800, lineHeight: 1.3 },
  body: { fontFamily: F.sans, fontSize: 15, lineHeight: 1.5 },
  small: { fontFamily: F.sans, fontSize: 13.5, lineHeight: 1.45 },
  caption: { fontFamily: F.sans, fontSize: 12.5, lineHeight: 1.4 },
  label: { fontFamily: F.sans, fontSize: 11.5, fontWeight: 800, letterSpacing: 0.9, lineHeight: 1.3, textTransform: "uppercase" },
  data: { fontFamily: F.mono, fontSize: 13, fontVariantNumeric: "tabular-nums" },
};

export const ELEVATION = {
  card: "0 2px 0 rgba(26,27,24,0.06)",
  raised: "0 4px 0 rgba(26,27,24,0.09)",
  overlay: "0 16px 40px rgba(28,29,26,0.22)",
  /* Y3 · two depths the first three could not express, added rather than
   * written inline again (the rule the design-system test now enforces).
   *
   * `overlayUp` is for a sheet docked to the BOTTOM edge: `overlay` falls
   * downward, i.e. off the bottom of the screen, so a docked sheet drawn with
   * it has no visible lift at all. The drill's feedback sheet needs this.
   * `badge` is for something sitting on a BOARD SQUARE rather than on a
   * surface — the move-quality badge has to stay legible over both the light
   * and the dark square colour, which needs a tight, dark shadow that none of
   * the surface elevations provide. */
  overlayUp: "0 -8px 28px rgba(28,29,26,0.16)",
  badge: "0 1px 4px rgba(0,0,0,0.3)",
};

/* A card: white (or a tone's ground), a hairline edge, R.lg corners, sitting on
 * its 2px edge. Add padding and margin at the call site. */
export const surface = (tone = null) => ({
  background: tone ? TONES[tone].ground : C.card,
  border: `1px solid ${tone ? TONES[tone].edge : C.edge}`,
  borderRadius: R.lg,
  boxShadow: ELEVATION.card,
});

/* A card that is itself the button: the same surface on a deeper edge, with a
 * button's defaults reset so it lays out like a card. */
export const tappable = (tone = null) => ({
  ...surface(tone),
  boxShadow: ELEVATION.raised,
  boxSizing: "border-box",
  color: C.ink,
  cursor: "pointer",
  display: "block",
  font: "inherit",
  textAlign: "left",
  width: "100%",
});

/* One chip, everywhere a small set of options sits in a row (the course
 * grid's side and sort, the Settings choices, the Ledger's filters). */
export const chip = (active) => ({
  alignItems: "center",
  background: active ? C.brassDim : C.card,
  border: `1.5px solid ${active ? C.brass : C.edge}`,
  borderRadius: R.pill,
  color: active ? C.brass : C.sub,
  cursor: "pointer",
  display: "inline-flex",
  flexShrink: 0,
  fontFamily: F.sans,
  fontSize: 13.5,
  fontWeight: 700,
  gap: 6,
  justifyContent: "center",
  minHeight: 36,
  padding: "0 14px",
  whiteSpace: "nowrap",
});

/* The dark violet hero ground, for the one card on a page that is the event
 * (the Daily Opening on Play, the summary's "Game on"). */
export const HERO = "linear-gradient(135deg, #2B1F4F 0%, #4A2FA8 100%)";

/* The one primary button: filled accent, a solid edge underneath, bold sans.
 * The drills, Rush, the Daily Opening, Swipe review and the session summary
 * grew it one by one; the home screen, the course page, the explorer and the
 * explainer sheets kept older flat and mono pills for the same job, so the
 * thing to press looked different on every screen. Spread it and add layout
 * (width, margin, padding) at the call site. COMPACT is for rows that share
 * their line with other content: a card header, the explorer's action row. */
export const PRIMARY = {
  background: C.brass,
  border: "none",
  borderRadius: R.md,
  boxShadow: `0 4px 0 ${C.brassDeep}`,
  color: C.onAccent,
  cursor: "pointer",
  fontFamily: F.sans,
  fontSize: 16,
  fontWeight: 800,
  minHeight: 50,
};

export const PRIMARY_COMPACT = {
  ...PRIMARY,
  boxShadow: `0 3px 0 ${C.brassDeep}`,
  fontSize: 14,
  minHeight: 44,
};

/* The second button: PRIMARY's shape and edge on a white face. For the other
 * choice beside a primary, or a card's action that is not the screen's job. */
export const SECONDARY = {
  background: C.card,
  border: `1.5px solid ${C.edge}`,
  borderRadius: R.md,
  boxShadow: `0 3px 0 ${C.edge}`,
  color: C.ink,
  cursor: "pointer",
  fontFamily: F.sans,
  fontSize: 16,
  fontWeight: 800,
  minHeight: 50,
};

export const SECONDARY_COMPACT = { ...SECONDARY, fontSize: 14, minHeight: 44 };

/* A text action: an accent word with no box and a real touch target. It
 * replaces the underlined links ("Done", "Close", "Skip setup") and the small
 * mono "→" links that were buttons in all but looks. */
export const GHOST = {
  alignItems: "center",
  background: "none",
  border: "none",
  color: C.brass,
  cursor: "pointer",
  display: "inline-flex",
  fontFamily: F.sans,
  fontSize: 14,
  fontWeight: 700,
  gap: 4,
  minHeight: 40,
  padding: "0 4px",
};

/* Board square + piece colors (used by the static MiniBoard and the interactive Board).
 * Interaction highlights follow lichess conventions so feedback feels familiar. */
export const BOARD = {
  light: "#F0D9B5",
  dark: "#B58863",
  whitePiece: "#F7F7F7",
  blackPiece: "#262421",
  whiteShadow: "0 1px 1px rgba(32,32,32,0.68)",
  blackShadow: "0 1px 1px rgba(255,255,255,0.34)",
  coordOnLight: "rgba(0,0,0,0.45)",
  coordOnDark: "rgba(255,255,255,0.78)",
  selected: "rgba(20,85,30,0.5)",
  target: "rgba(20,85,30,0.32)",
  lastMove: "rgba(155,199,0,0.41)",
  wrong: "rgba(214,69,48,0.5)",
  right: "rgba(31,157,99,0.55)",
  arrowHint: "rgba(108,60,233,0.85)", // suggested-move arrow (the accent, `brass`)
  arrowRight: "rgba(31,157,99,0.9)", // reveal-move arrow after a miss
  /* The interactive drill's other voices on the board. Warm for the opponent
   * (their options fanned out before they choose), sky for "what if I played
   * this" when a candidate chip is tapped, red for the engine's punishment of
   * a move just played. Four hues, four meanings, none of them reusing the
   * green/red that grades the user's own answer. */
  arrowOpponent: "rgba(222,120,40,0.85)",
  arrowPreview: "rgba(40,132,214,0.88)",
  arrowThreat: "rgba(214,69,48,0.85)",
};

/* Move-quality badges — the chess.com vocabulary players already read, drawn
 * on the destination square and repeated in the feedback sheet so the two can
 * never disagree. `glyph` is text on purpose (annotation marks ARE text in
 * chess); `book` alone has no conventional mark and gets an icon in the
 * component that draws it. Labels are dictionary keys, resolved on read. */
export const QUALITY = {
  book: { color: "#9C7A4F", glyph: null, label: "quality.book" },
  best: { color: "#1F9D63", glyph: "★", label: "quality.best" },
  excellent: { color: "#2FA873", glyph: "!", label: "quality.excellent" },
  good: { color: "#6E9F52", glyph: "✓", label: "quality.good" },
  inaccuracy: { color: "#DDA02A", glyph: "?!", label: "quality.inaccuracy" },
  mistake: { color: "#E0782F", glyph: "?", label: "quality.mistake" },
  blunder: { color: "#D64530", glyph: "??", label: "quality.blunder" },
};

/* Static (non-interactive) uses — the Ledger's tiny opening-tree glyphs —
 * still render as text, so the glyph set survives independent of the board. */
export const PIECES = {
  wK: "♚", wQ: "♛", wR: "♜", wB: "♝", wN: "♞", wP: "♟",
  bK: "♚", bQ: "♛", bR: "♜", bB: "♝", bN: "♞", bP: "♟",
};

/* ---- user-selectable board looks (Settings) ----
 * Each preset only overrides square colors; interaction highlight colors stay
 * shared so feedback reads identically on every board. */
export const BOARD_PRESETS = {
  /* `label` is a dictionary KEY, not a name: this is a module constant, so
   * a resolved string would freeze at the locale it was imported under. */
  walnut: { label: "board.walnut", light: "#F0D9B5", dark: "#B58863" },
  tournament: { label: "board.tournament", light: "#D7CCAA", dark: "#6E8A70" },
  slate: { label: "board.slate", light: "#DEE3E6", dark: "#8CA2AD" },
  midnight: { label: "board.midnight", light: "#9FA8B8", dark: "#4A5568" },
};

/* Piece art: the "cburnett" SVG set (by Colin M.L. Burnett — see
 * public/pieces/cburnett/CREDITS.txt), the same style most chess sites use.
 * Board.jsx always renders real piece art now, so there is no glyph fallback
 * to pick between — this stays a single-entry map so a second licensed set
 * could be added later without changing the board's plumbing. */
export const PIECE_SETS = {
  cburnett: { label: "Standard", svg: "/pieces/cburnett" },
};

export function boardLook(settings) {
  const preset = BOARD_PRESETS[settings?.boardTheme] || BOARD_PRESETS.walnut;
  const pieces = PIECE_SETS[settings?.pieceSet] || PIECE_SETS.cburnett;
  // `glyphs` is only for text-rendered contexts (the Ledger's static mini
  // boards) — the interactive Board.jsx always uses the SVG set (`svg`).
  return { ...BOARD, light: preset.light, dark: preset.dark, svg: pieces.svg, glyphs: PIECES };
}
